import { MessageInstagramDmKind } from "@redotech/crdb-enums/shopper-ai/message-instagram-dm-kind";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type {
  HasTagCondition,
  MessageBodyMatchesCondition,
  MessageSentimentCondition,
  SubjectLineCondition,
  TopicCondition,
  TriggerTagCondition,
} from "../rules/schemas";
import {
  AssignmentTypeOption,
  hasTagConditionSchema,
  messageBodyMatchesConditionSchema,
  messageSentimentConditionSchema,
  ruleActionSchema,
  subjectLineConditionSchema,
  topicConditionSchema,
  triggerTagConditionSchema,
} from "../rules/schemas";
import {
  DateQueryOperator,
  ErrorHandlingStrategy,
  NumericQueryOperator,
  QueryOperator,
  RuleConditionType,
  dateQueryOperators,
  selectionQueryOperators,
  textQueryOperators,
} from "../rules/types";
import {
  AI_STATUSES,
  CONVERSATION_STATUSES_V3,
  MESSAGE_CHANNELS_V3,
  MESSAGE_COUNT_SENDER_TYPES_V3,
  MESSAGE_TYPE_CHANNELS_V3,
  SHOPPER_AGENT_OUTREACH_CONDITION_VALUES,
  TimeUnit,
} from "./types";

// V3-specific: TICKET_CHANNEL uses MessageChannelV3 strings
const ticketChannelV3ConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_CHANNEL),
  channels: z.array(z.enum(MESSAGE_CHANNELS_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const ticketChannelV3ConditionSchema: z.ZodType<TicketChannelV3Condition> =
  ticketChannelV3ConditionSchema_;
export interface TicketChannelV3Condition extends z.infer<
  typeof ticketChannelV3ConditionSchema_
> {}

// V3-specific: TICKET_STATUS uses ConversationStatusV3 strings.
// The full schema (including the "has_been" variant) is assembled after
// ResponseTimeOperator is declared below; re-exported from the bottom of this
// file as ticketStatusV3ConditionSchema / TicketStatusV3Condition.
const ticketStatusSelectionConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(z.enum(CONVERSATION_STATUSES_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

// PREVIOUS_TICKET_STATUS — checks the status the ticket had before the
// triggering transition. Only meaningful under a CONVERSATION_STATUS_CHANGED
// trigger (reads triggerDetails.previousStatus); evaluates to false when the
// trigger carries no previousStatus.
const previousTicketStatusConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.PREVIOUS_TICKET_STATUS),
  statuses: z.array(z.enum(CONVERSATION_STATUSES_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const previousTicketStatusConditionSchema: z.ZodType<PreviousTicketStatusCondition> =
  previousTicketStatusConditionSchema_;
export interface PreviousTicketStatusCondition extends z.infer<
  typeof previousTicketStatusConditionSchema_
> {}

// V3-specific: AI_STATUS — checks the AI engagement status recorded on the
// conversation's thread product-data rows (both `shopper_ai` and `support`).
const aiStatusConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.AI_STATUS),
  statuses: z.array(z.enum(AI_STATUSES)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const aiStatusConditionSchema: z.ZodType<AiStatusCondition> =
  aiStatusConditionSchema_;
export interface AiStatusCondition extends z.infer<
  typeof aiStatusConditionSchema_
> {}

const assignmentV3ConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.ASSIGNMENT),
  mode: z.discriminatedUnion("_tag", [
    z.object({
      _tag: z.literal("type"),
      assignmentTypes: z.array(createEnum(AssignmentTypeOption)),
    }),
    z.object({
      _tag: z.literal("specific_users"),
      mongoUserIds: z.array(z.string()),
    }),
    z.object({
      _tag: z.literal("user_group"),
      userGroupIds: z.array(z.string()),
    }),
  ]),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const assignmentV3ConditionSchema: z.ZodType<AssignmentV3Condition> =
  assignmentV3ConditionSchema_;
export interface AssignmentV3Condition extends z.infer<
  typeof assignmentV3ConditionSchema_
> {}
export type AssignmentV3ConditionMode = AssignmentV3Condition["mode"];

/**
 * Last Order Condition (V3)
 *
 * Replaces V2's LAST_ORDER_TIME with a more versatile condition that supports
 * multiple order properties: time, value, and item count.
 */
export enum LastOrderProperty {
  TIME = "time",
  VALUE = "value",
  ITEM_COUNT = "item_count",
}

const lastOrderTimePropertySchema = z.object({
  conditionType: z.literal(RuleConditionType.LAST_ORDER),
  property: z.literal(LastOrderProperty.TIME),
  queryOperator: z
    .enum(dateQueryOperators)
    .default(DateQueryOperator.WITHIN_LAST),
  value: z.number().optional(),
  unit: z.enum(TimeUnit).default(TimeUnit.DAYS),
  date: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

function numericPropertySchemas<T extends LastOrderProperty>(property: T) {
  const base = {
    conditionType: z.literal(RuleConditionType.LAST_ORDER),
    property: z.literal(property),
  };
  return [
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.GREATER_THAN),
      value: z.number(),
    }),
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.LESS_THAN),
      value: z.number(),
    }),
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.EQUAL_TO),
      value: z.number(),
    }),
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.BETWEEN),
      minValue: z.number(),
      maxValue: z.number(),
    }),
  ] as const;
}

const lastOrderValuePropertySchemas = numericPropertySchemas(
  LastOrderProperty.VALUE,
);
const lastOrderItemCountPropertySchemas = numericPropertySchemas(
  LastOrderProperty.ITEM_COUNT,
);

const lastOrderConditionSchema_ = z.union([
  lastOrderTimePropertySchema,
  ...lastOrderValuePropertySchemas,
  ...lastOrderItemCountPropertySchemas,
]);
export const lastOrderConditionSchema: z.ZodType<LastOrderCondition> =
  lastOrderConditionSchema_;
export type LastOrderCondition = z.infer<typeof lastOrderConditionSchema_>;

/**
 * Time-Based Condition (V3)
 *
 * Replaces V2's AFTER_HOURS with a more versatile condition supporting:
 * - Business hours: checks against team's configured business hours
 * - Weekly schedule: recurring per-day time blocks (e.g. MWF 2-5pm)
 * - Specific date: one-time before/after/between specific dates
 */
export enum TimeBasedMode {
  BUSINESS_HOURS = "business_hours",
  WEEKLY_SCHEDULE = "weekly_schedule",
  SPECIFIC_DATE = "specific_date",
}

export enum SpecificDateOperator {
  BEFORE = "before",
  AFTER = "after",
  BETWEEN = "between",
}

const timeBlockSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
});

const dayScheduleSchema = z.object({
  enabled: z.boolean().default(false),
  timeBlocks: z.array(timeBlockSchema).default([]),
});

const weeklyScheduleSchema = z.object({
  sunday: dayScheduleSchema.optional(),
  monday: dayScheduleSchema.optional(),
  tuesday: dayScheduleSchema.optional(),
  wednesday: dayScheduleSchema.optional(),
  thursday: dayScheduleSchema.optional(),
  friday: dayScheduleSchema.optional(),
  saturday: dayScheduleSchema.optional(),
});

const timeBasedBusinessHoursSchema = z.object({
  conditionType: z.literal(RuleConditionType.TIME_BASED),
  mode: z.literal(TimeBasedMode.BUSINESS_HOURS),
});

const timeBasedWeeklyScheduleSchema = z.object({
  conditionType: z.literal(RuleConditionType.TIME_BASED),
  mode: z.literal(TimeBasedMode.WEEKLY_SCHEDULE),
  timezone: z.string().optional(),
  schedule: weeklyScheduleSchema.optional(),
});

const timeBasedSpecificDateSchema = z.object({
  conditionType: z.literal(RuleConditionType.TIME_BASED),
  mode: z.literal(TimeBasedMode.SPECIFIC_DATE),
  dateOperator: z
    .enum(SpecificDateOperator)
    .default(SpecificDateOperator.AFTER),
  timezone: z.string().optional(),
  dateTime: z.string().optional(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
});

const timeBasedConditionSchema_ = z.union([
  timeBasedBusinessHoursSchema,
  timeBasedWeeklyScheduleSchema,
  timeBasedSpecificDateSchema,
]);
export const timeBasedConditionSchema: z.ZodType<TimeBasedCondition> =
  timeBasedConditionSchema_;
export type TimeBasedCondition = z.infer<typeof timeBasedConditionSchema_>;

/**
 * Response Time Condition (V3)
 *
 * Evaluates time elapsed since the last response from merchant, customer, or either.
 */
export enum ResponseType {
  MERCHANT = "merchant",
  CUSTOMER = "customer",
  EITHER = "either",
}

export enum ResponseTimeOperator {
  MORE_THAN = "more_than",
  LESS_THAN = "less_than",
  BETWEEN = "between",
}

const responseTimeBaseFields = {
  conditionType: z.literal(RuleConditionType.RESPONSE_TIME),
  responseType: z.enum(ResponseType).default(ResponseType.EITHER),
  unit: z.enum(TimeUnit).default(TimeUnit.HOURS),
};

const responseTimeMoreThanSchema = z.object({
  ...responseTimeBaseFields,
  responseTimeOperator: z.literal(ResponseTimeOperator.MORE_THAN),
  value: z.number(),
});

const responseTimeLessThanSchema = z.object({
  ...responseTimeBaseFields,
  responseTimeOperator: z.literal(ResponseTimeOperator.LESS_THAN),
  value: z.number(),
});

const responseTimeBetweenSchema = z.object({
  ...responseTimeBaseFields,
  responseTimeOperator: z.literal(ResponseTimeOperator.BETWEEN),
  minValue: z.number(),
  maxValue: z.number(),
});

const responseTimeConditionSchema_ = z.discriminatedUnion(
  "responseTimeOperator",
  [
    responseTimeMoreThanSchema,
    responseTimeLessThanSchema,
    responseTimeBetweenSchema,
  ],
);
export const responseTimeConditionSchema: z.ZodType<ResponseTimeCondition> =
  responseTimeConditionSchema_;
export type ResponseTimeCondition = z.infer<
  typeof responseTimeConditionSchema_
>;

/**
 * Ticket Status Condition — "has been" variant (V3)
 *
 * Checks whether the ticket has been in one of the specified statuses for
 * more than / less than / between X time units (using statusUpdatedAt).
 * Assembled here so it can reuse ResponseTimeOperator defined above.
 */
const ticketStatusHasBeenBaseFields = {
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(z.enum(CONVERSATION_STATUSES_V3)),
  queryOperator: z.literal(QueryOperator.HAS_BEEN),
  unit: z.enum(TimeUnit).default(TimeUnit.HOURS),
};

const ticketStatusHasBeenMoreThanSchema = z.object({
  ...ticketStatusHasBeenBaseFields,
  statusTimeOperator: z.literal(ResponseTimeOperator.MORE_THAN),
  value: z.number(),
});

const ticketStatusHasBeenLessThanSchema = z.object({
  ...ticketStatusHasBeenBaseFields,
  statusTimeOperator: z.literal(ResponseTimeOperator.LESS_THAN),
  value: z.number(),
});

const ticketStatusHasBeenBetweenSchema = z.object({
  ...ticketStatusHasBeenBaseFields,
  statusTimeOperator: z.literal(ResponseTimeOperator.BETWEEN),
  minValue: z.number(),
  maxValue: z.number(),
});

const ticketStatusHasBeenConditionSchema_ = z.discriminatedUnion(
  "statusTimeOperator",
  [
    ticketStatusHasBeenMoreThanSchema,
    ticketStatusHasBeenLessThanSchema,
    ticketStatusHasBeenBetweenSchema,
  ],
);
export const ticketStatusHasBeenConditionSchema: z.ZodType<TicketStatusHasBeenCondition> =
  ticketStatusHasBeenConditionSchema_;
export type TicketStatusHasBeenCondition = z.infer<
  typeof ticketStatusHasBeenConditionSchema_
>;

const ticketStatusV3ConditionSchema_ = z.union([
  ticketStatusSelectionConditionSchema,
  ticketStatusHasBeenConditionSchema_,
]);
export const ticketStatusV3ConditionSchema: z.ZodType<TicketStatusV3Condition> =
  ticketStatusV3ConditionSchema_;
export type TicketStatusV3Condition = z.infer<
  typeof ticketStatusV3ConditionSchema_
>;

/**
 * Message Count Condition (V3)
 *
 * Evaluates the number of messages on a support ticket against a numeric
 * threshold. By default it counts every non-deleted message linked to the
 * ticket's topic; sender types can optionally scope that count.
 */
const messageCountBase = {
  conditionType: z.literal(RuleConditionType.MESSAGE_COUNT),
  senderTypes: z.array(z.enum(MESSAGE_COUNT_SENDER_TYPES_V3)).optional(),
};

const messageCountGreaterThanSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.GREATER_THAN),
  value: z.number(),
});

const messageCountLessThanSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.LESS_THAN),
  value: z.number(),
});

const messageCountEqualToSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.EQUAL_TO),
  value: z.number(),
});

const messageCountBetweenSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.BETWEEN),
  minValue: z.number(),
  maxValue: z.number(),
});

const messageCountConditionSchema_ = z.discriminatedUnion("queryOperator", [
  messageCountGreaterThanSchema,
  messageCountLessThanSchema,
  messageCountEqualToSchema,
  messageCountBetweenSchema,
]);
export const messageCountConditionSchema: z.ZodType<MessageCountCondition> =
  messageCountConditionSchema_;
export type MessageCountCondition = z.infer<
  typeof messageCountConditionSchema_
>;

/**
 * Sent From / Received At Conditions (V3)
 *
 * Replaces V2's SENT_FROM_EMAIL and RECEIVED_AT_EMAIL with more generic
 * conditions. Currently supports email channel, designed to be extensible
 * for additional channels in the future.
 */
export enum ContactChannel {
  EMAIL = "email",
}

const sentFromConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.SENT_FROM),
  channel: z.enum(ContactChannel).default(ContactChannel.EMAIL),
  values: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});
export const sentFromConditionSchema: z.ZodType<SentFromCondition> =
  sentFromConditionSchema_;
export interface SentFromCondition extends z.infer<
  typeof sentFromConditionSchema_
> {}

const receivedAtConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.RECEIVED_AT),
  channel: z.enum(ContactChannel).default(ContactChannel.EMAIL),
  values: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.IS_EXACTLY),
});
export const receivedAtConditionSchema: z.ZodType<ReceivedAtCondition> =
  receivedAtConditionSchema_;
export interface ReceivedAtCondition extends z.infer<
  typeof receivedAtConditionSchema_
> {}

const messageTypeV3ConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.MESSAGE_TYPE),
  messageTypes: z.array(z.enum(MESSAGE_TYPE_CHANNELS_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const messageTypeV3ConditionSchema: z.ZodType<MessageTypeV3Condition> =
  messageTypeV3ConditionSchema_;
export interface MessageTypeV3Condition extends z.infer<
  typeof messageTypeV3ConditionSchema_
> {}

/**
 * Random Percentage Condition (V3)
 *
 * Evaluates to true with the given probability. For example, a percentage of 50
 * means the condition passes approximately 50% of the time. Used to randomly
 * sample rule execution (e.g. "have the AI respond to only 50% of tickets").
 */
const randomPercentageConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.RANDOM_PERCENTAGE),
  percentage: z.number().min(0).max(100).default(50),
});
export const randomPercentageConditionSchema: z.ZodType<RandomPercentageCondition> =
  randomPercentageConditionSchema_;
export interface RandomPercentageCondition extends z.infer<
  typeof randomPercentageConditionSchema_
> {}

/**
 * Passes when the thread is the live lane of a `shopper_agent_conversation`
 * topic and the team may reply. System-managed; not in the merchant picker.
 */
const shopperAgentConversationConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.SHOPPER_AGENT_CONVERSATION),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const shopperAgentConversationConditionSchema: z.ZodType<ShopperAgentConversationCondition> =
  shopperAgentConversationConditionSchema_;
export interface ShopperAgentConversationCondition extends z.infer<
  typeof shopperAgentConversationConditionSchema_
> {}

const shopperAgentOutreachConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.SHOPPER_AGENT_OUTREACH),
  outreachTypes: z.array(z.enum(SHOPPER_AGENT_OUTREACH_CONDITION_VALUES)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const shopperAgentOutreachConditionSchema: z.ZodType<ShopperAgentOutreachCondition> =
  shopperAgentOutreachConditionSchema_;
export interface ShopperAgentOutreachCondition extends z.infer<
  typeof shopperAgentOutreachConditionSchema_
> {}

const userIsAvailableConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.USER_IS_AVAILABLE),
  queryOperator: z
    .union([z.literal(QueryOperator.IS), z.literal(QueryOperator.IS_NOT)])
    .optional(),
});
export interface UserIsAvailableCondition extends z.infer<
  typeof userIsAvailableConditionSchema_
> {}
export const userIsAvailableConditionSchema: z.ZodType<UserIsAvailableCondition> =
  userIsAvailableConditionSchema_;

/** Passes when the triggering message is an Instagram story mention/reply of one
 * of the given kinds (read from message_instagram_dm). System-managed. */
const messageStoryKindConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.MESSAGE_STORY_KIND),
  storyKinds: z.array(z.enum(MessageInstagramDmKind)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const messageStoryKindConditionSchema: z.ZodType<MessageStoryKindCondition> =
  messageStoryKindConditionSchema_;
export interface MessageStoryKindCondition extends z.infer<
  typeof messageStoryKindConditionSchema_
> {}

export type RuleV3Condition =
  | TicketChannelV3Condition
  | TicketStatusV3Condition
  | TopicCondition
  | SentFromCondition
  | ReceivedAtCondition
  | SubjectLineCondition
  | MessageBodyMatchesCondition
  | TimeBasedCondition
  | AssignmentV3Condition
  | LastOrderCondition
  | MessageSentimentCondition
  | HasTagCondition
  | TriggerTagCondition
  | ResponseTimeCondition
  | AiStatusCondition
  | MessageCountCondition
  | MessageTypeV3Condition
  | RandomPercentageCondition
  | PreviousTicketStatusCondition
  | ShopperAgentConversationCondition
  | ShopperAgentOutreachCondition
  | UserIsAvailableCondition
  | MessageStoryKindCondition;
export const ruleV3ConditionSchema: z.ZodType<RuleV3Condition> = z.union([
  userIsAvailableConditionSchema_,
  ticketChannelV3ConditionSchema_,
  ticketStatusV3ConditionSchema_,
  topicConditionSchema,
  sentFromConditionSchema_,
  receivedAtConditionSchema_,
  subjectLineConditionSchema,
  messageBodyMatchesConditionSchema,
  timeBasedConditionSchema_,
  assignmentV3ConditionSchema_,
  lastOrderConditionSchema_,
  messageSentimentConditionSchema,
  hasTagConditionSchema,
  triggerTagConditionSchema,
  responseTimeConditionSchema_,
  aiStatusConditionSchema_,
  messageCountConditionSchema_,
  messageTypeV3ConditionSchema_,
  randomPercentageConditionSchema_,
  previousTicketStatusConditionSchema_,
  shopperAgentConversationConditionSchema_,
  shopperAgentOutreachConditionSchema_,
  messageStoryKindConditionSchema_,
]);

const conditionGroupV3Operator = z.enum(["AND", "OR"]);
export type ConditionGroupV3Operator = z.infer<typeof conditionGroupV3Operator>;

const conditionGroupV3Schema_: z.ZodType<ConditionGroupV3> = z.lazy(() =>
  z.object({
    operator: conditionGroupV3Operator,
    conditions: z.array(
      z.union([ruleV3ConditionSchema, conditionGroupV3Schema_]),
    ),
  }),
);
export const conditionGroupV3Schema: z.ZodType<ConditionGroupV3> =
  conditionGroupV3Schema_;

export interface ConditionGroupV3 {
  operator: ConditionGroupV3Operator;
  conditions: (RuleV3Condition | ConditionGroupV3)[];
}

// Base step schema
const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: z.string(),
  id: z.string(),
});

const ruleV3ConditionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_CONDITIONS_V3),
  conditionGroups: conditionGroupV3Schema,
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});
export const ruleV3ConditionsStepSchema: z.ZodType<RuleV3ConditionsStep> =
  ruleV3ConditionsStepSchema_;
export interface RuleV3ConditionsStep extends z.infer<
  typeof ruleV3ConditionsStepSchema_
> {}

const ruleV3ActionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_ACTIONS_V3),
  actions: z.array(ruleActionSchema),
  errorHandlingStrategy: createEnum(ErrorHandlingStrategy).default(
    ErrorHandlingStrategy.FAIL_FAST,
  ),
  nextId: z.string().optional(),
});
export const ruleV3ActionsStepSchema: z.ZodType<RuleV3ActionsStep> =
  ruleV3ActionsStepSchema_;
export interface RuleV3ActionsStep extends z.infer<
  typeof ruleV3ActionsStepSchema_
> {}
